import { Nft } from "@/api/nfts/types";
import { Order } from "@/api/orders/types";
import { Button } from "@/app/(components)/Basic/Button";
import { CryptoValue } from "@/app/(components)/Basic/CryptoValue";
import useAccountBalance from "@/shared/hooks/useAccountBalance";
import useTotalTakerPrice from "@/shared/hooks/useTotalTakerPrice";
import { useState } from "react";
import { NftDetailsListItem } from "../NftDetailsListItem";
import { NftModalBase } from "../NftModalBase";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import { twMerge } from "tailwind-merge";
import usePaymentCurrency from "@/shared/hooks/usePaymentCurrency";
import { getFeePercentage } from "@/shared/currencies";
import { SellerGet } from "../../Sales/Forms/CreateFixedAuction/hooks/useSellerGet";
import { useWeb3State } from "@/web3/context";
import { ZeroAddress } from "ethers";

export interface Props {
  nft: Nft;
  order: Order;
  sellerGet: SellerGet;
  onPurchase: () => Promise<void>;
  onClose: () => void;
  buttonText: string;
  title: string;
  isLoading: boolean;
  error?: string;
  shouldShowBalance?: boolean;
  displaySellerFees?: boolean;
}

export default function NftCompletePurchaseDialog({
  nft,
  order,
  sellerGet,
  onPurchase,
  onClose,
  buttonText,
  title,
  isLoading,
  error,
  shouldShowBalance = true,
  displaySellerFees = false,
}: Props) {
  const [isOpen, setIsOpen] = useState(true);
  const { currentAddress } = useWeb3State();
  const { balanceInCrypto, balanceInUSD } = useAccountBalance(
    order.paymentErc20TokenAddress,
  );

  const paymentMethod = usePaymentCurrency(order.paymentErc20TokenAddress);

  const { totalPrice, totalPriceInUSD } = useTotalTakerPrice({
    price: order.price,
    paymentErc20TokenAddress: order.paymentErc20TokenAddress,
    takerFee: order.takerFee,
  });

  const onSubmitOrder = async () => {
    await onPurchase();
  };

  return (
    <NftModalBase
      hasCloseButton={!isLoading}
      disableClose={isLoading}
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
        onClose();
      }}
      title={title}
      nft={nft}
    >
      {shouldShowBalance ? (
        <NftDetailsListItem
          small
          label="Your balance"
          allThemesWithWhiteBackground
        >
          <div className={twMerge("flex items-center justify-end flex-wrap")}>
            {balanceInCrypto} {paymentMethod?.label}
            <span className="text-secondary text-body-3 ml-2 endemic:text-disabled">
              ({balanceInUSD} USD)
            </span>
          </div>
        </NftDetailsListItem>
      ) : null}
      <NftDetailsListItem small label="Price" allThemesWithWhiteBackground>
        <CryptoValue
          oneLine
          hideIcon
          cryptoValue={order.price}
          paymentErc20TokenAddress={order.paymentErc20TokenAddress}
          usdPriceClassName="ml-2 text-body-3 endemic:text-disabled"
        />
      </NftDetailsListItem>
      <NftDetailsListItem
        small
        label="Service fee"
        allThemesWithWhiteBackground
      >
        {getFeePercentage(
          order.seller === currentAddress || order.seller === ZeroAddress
            ? order.makerFee!
            : order.takerFee!,
        )}
        %
      </NftDetailsListItem>
      {displaySellerFees && (
        <>
          <NftDetailsListItem
            small
            label="Royalties"
            allThemesWithWhiteBackground
          >
            {getFeePercentage(order.royaltiesFee || 0)}%
          </NftDetailsListItem>
          <NftDetailsListItem
            small
            label="Collective fee"
            allThemesWithWhiteBackground
          >
            {getFeePercentage(order.collectiveFee || 0)}%
          </NftDetailsListItem>
        </>
      )}
      {order.seller !== currentAddress && order.seller !== ZeroAddress ? (
        <NftDetailsListItem
          small
          label="You will pay"
          allThemesWithWhiteBackground
        >
          <div className="flex items-center justify-end text-right flex-wrap">
            {totalPrice?.rounded} {paymentMethod?.label}
            <span className="text-secondary text-body-3 ml-2 endemic:text-disabled">
              ({totalPriceInUSD} USD)
            </span>
          </div>
        </NftDetailsListItem>
      ) : null}
      {order.seller === currentAddress || order.seller === ZeroAddress ? (
        <NftDetailsListItem
          small
          label="You will receive"
          allThemesWithWhiteBackground
        >
          <div className="flex items-center justify-end text-right flex-wrap">
            {sellerGet?.cryptoValue} {sellerGet?.cryptoName}
            <span className="text-secondary text-body-3 ml-2 endemic:text-disabled">
              ({sellerGet?.usdValue} USD)
            </span>
          </div>
        </NftDetailsListItem>
      ) : null}
      <div className="flex justify-center mt-10">
        <Button
          className="mx-2"
          onClick={onSubmitOrder}
          disabled={isLoading || !!error}
          endIcon={<ArrowRightIcon className="w-4" />}
        >
          {isLoading ? "Loading" : buttonText}
        </Button>
      </div>
      {error && (
        <div className="flex justify-center mt-3 absolute w-full text-center pb-2">
          <span className="text-red endemic:text-white font-semibold text-xs px-4 block h-0">
            {error}
          </span>
        </div>
      )}
    </NftModalBase>
  );
}
